import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback
} from "react";
import propTypes from "prop-types";
import classnames from "classnames";

import InputMask from "react-input-mask"; //https://www.npmjs.com/package/react-input-mask-for-date
import { DayPicker } from "react-day-picker";

import { isValidDate } from "../../utilities/dates";
import { useField } from "../form/Form";
import { formatDate } from "../formatDate/FormatDate";
import { createDataAttributes } from "../../utilities";

import Label from "./Label";
import ErrorsForField from "../form/ErrorsForField";
import Icon from "../icons/Icon";

import "./DatePicker.scss";

// React Day Picker
// https://react-day-picker.js.org/api/DayPickerInput
// Currently using fork: https://github.com/UFGInsurance/react-day-picker

// React Input Mask
// https://github.com/text-mask/text-mask/tree/master/react#readme

// This needs to remain a class component otherwise we receive
// a console error that `Function components cannot be given refs. Attempts to access this ref will fail`.
// Offending line in react-day-picker: https://bit.ly/2Sz3dqv
// eslint-disable-next-line react/prefer-stateless-function

const Calendar = ({
  value,
  handleDaySelect,
  show,
  setShow,
  mainRef,
  minDate,
  maxDate
}) => {
  const today = new Date();

  const fv = value?.from ? formatDate(value.from) : "";
  const tv = value?.to ? formatDate(value.to) : "";

  const v = { from: fv, to: tv };

  const leftMonth = isValidDate(value?.from)
    ? fv
    : isValidDate(value?.to)
    ? tv
    : today.setMonth(today.getMonth() - 1);

  const [pos, setPos] = useState({});
  const [hoveredDay, setHoveredDay] = useState();
  const [currentMonth, setCurrentMonth] = useState(leftMonth);

  useEffect(() => {
    if (isValidDate(value)) setCurrentMonth(new Date(value));
  }, [value]);

  const popperRef = useRef(null);

  // handle clicking outside of the popper to close the popper
  useEffect(() => {
    const handleClickOutside = event => {
      if (mainRef.current && !mainRef.current.contains(event.target))
        setShow(null);
    };
    const escKey = evt => {
      evt = evt || window.event;
      if (evt.keyCode === 27) setShow(null);
    };
    document.addEventListener("keydown", escKey, true);
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
      document.removeEventListener("keydown", escKey, true);
    };
  }, [mainRef, setShow]);

  // handle the position of the popper in case it doesn't fit in the viewport when the component renders
  useLayoutEffect(() => {
    const mainRect = mainRef?.current
      ? mainRef.current.getBoundingClientRect()
      : {};

    const popperRect = popperRef?.current
      ? popperRef.current.getBoundingClientRect()
      : {};

    const popperHeight = popperRect?.height;
    const spaceBelow = window.innerHeight - (mainRect?.bottom || 0);
    const spaceRight = window.innerWidth - (mainRect?.right || 0);
    const popperWidth = popperRect?.width;
    const showAbove = popperHeight && spaceBelow < popperHeight;
    const showLeft = popperWidth && spaceRight < popperWidth;

    if (mainRect && popperRect)
      setPos({
        top: showAbove
          ? "-" + (popperRect.height + 10) + "px"
          : mainRect.height + 5 + "px",
        right: showLeft ? 0 : "auto",
        showAbove,
        showLeft
      });
  }, [mainRef]);

  const classes = classnames(
    "uikit__datepicker__calendar-wrapper",
    "uikit__datepicker__calendar-wrapper-range",
    {
      uikit__datepicker__calendar__showAbove: pos?.showAbove,
      uikit__datepicker__calendar__showLeft: pos?.showLeft
    }
  );

  const dataPos = [
    pos?.showAbove ? "above" : "below",
    pos?.showLeft ? "left" : "right"
  ];

  return (
    <div
      ref={popperRef}
      className={classes}
      style={pos}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label="Date Picker Calendar"
    >
      <DayPicker
        data-pos={dataPos.join("-")}
        mode="single"
        className="uikit__datepicker__calendar"
        month={currentMonth}
        onMonthChange={setCurrentMonth}
        numberOfMonths={2}
        modifiers={{
          from: fv,
          to: tv,
          between: d => isValidDate(fv) && isValidDate(tv) && d > fv && d < tv,
          hovered: d => hoveredDay && d.getTime() === hoveredDay.getTime(),
          hoveredBefore: d => hoveredDay && d < hoveredDay,
          hoveredAfter: d => hoveredDay && d > hoveredDay,
          afterStart: d =>
            hoveredDay && isValidDate(fv) && hoveredDay > d && d > fv,
          beforeEnd: d =>
            hoveredDay && isValidDate(tv) && hoveredDay < d && d < tv
        }}
        modifiersClassNames={{
          from: "uikit__datepicker__selected-from",
          to: "uikit__datepicker__selected-to",
          hovered: "uikit__datepicker__selected-hovered",
          hoveredBefore: "uikit__datepicker__selected-hovered-before",
          hoveredAfter: "uikit__datepicker__selected-hovered-after",
          between: "uikit__datepicker__selected-between",
          afterStart: "uikit__datepicker__range-start-after",
          beforeEnd: "uikit__datepicker__range-end-before"
        }}
        onDayMouseEnter={d => setHoveredDay(d)}
        onDayMouseLeave={() => setHoveredDay(null)}
        selected={v}
        onDayClick={handleDaySelect}
        show={show}
        disabled={day =>
          (minDate && day < new Date(minDate)) ||
          (maxDate && day > new Date(maxDate))
        }
      />
    </div>
  );
};

const DateRange = ({
  id,
  name,
  labelFrom,
  labelTo,
  value,
  maxDate,
  minDate,
  disabled,
  onChange,
  onBlur,
  onValidate,
  onKeyDown,
  required,
  placeholder,
  format,
  hideLabel,
  noLabel,
  requiredError,
  fieldErrors,
  hideErrors,
  dataFromAttrib,
  dataToAttrib,
  dataWrapperAttrib,
  showPreviousMonths,
  ...rest
}) => {
  const [show, setShow] = useState();

  const mainRef = useRef(null);

  const min = minDate ? new Date(minDate).setHours(0, 0, 0, 0) : null;
  const max = maxDate ? new Date(maxDate).setHours(0, 0, 0, 0) : null;

  /*
  --------
  HANDLERS
  --------
  */

  const handleFromFocus = () => setShow("from");

  const handleToFocus = () => setShow("to");

  const handleFromBlur = useCallback(
    event => {
      const fromValue =
        event?.target?.value ||
        mainRef.current.querySelector(`#${id}-from`).value;

      // only run onBlur if the calendar picker isn't visible
      if (!show) {
        onBlur({
          field: name,
          value: {
            ...value,
            from: formatDate(fromValue, format)
          }
        });
      }
    },
    [format, id, name, onBlur, show, value]
  );

  const handleToBlur = useCallback(
    event => {
      const toValue =
        event?.target?.value ||
        mainRef.current.querySelector(`#${id}-to`).value;

      // only run onBlur if the calendar picker isn't visible
      if (!show)
        onBlur({
          field: name,
          value: {
            ...value,
            to: formatDate(toValue, format)
          }
        });
    },
    [format, id, name, onBlur, show, value]
  );

  const handleFromChange = event => {
    const raw = event.target.value;
    const v = isValidDate(raw) ? formatDate(raw, format) : raw;
    onChange({
      field: name,
      value: { ...value, from: v }
    });
  };

  const handleToChange = event => {
    const raw = event.target.value;
    const v = isValidDate(raw) ? formatDate(raw, format) : raw;
    onChange({
      field: name,
      value: { ...value, to: v }
    });
  };

  const handleFromClear = () => {
    onChange({
      field: name,
      value: { ...value, from: "" }
    });
    setShow(null);
  };

  const handleToClear = () => {
    onChange({
      field: name,
      value: { ...value, to: "" }
    });
    setShow(null);
  };

  const handleKeyDown = e => {
    if (
      (show === "to" && e.key === "Tab") ||
      (show === "from" && e.key === "Tab" && e.shiftKey)
    )
      setShow(null);
  };

  const handleDaySelect = date => {
    const { from, to } = value;
    const val = {};
    const v = {
      from: isValidDate(from) ? new Date(formatDate(from)) : "",
      to: isValidDate(to) ? new Date(formatDate(to)) : ""
    };
    if (date) {
      if (show === "from") {
        val.from = formatDate(date, format);
        val.to = formatDate(v.to, format);

        setShow("to");

        // if new 'from' value is after the existing 'to' value, then reset the 'to' value
        if (!!v.to && date > v.to) val.to = "";
      }
      if (show === "to") {
        val.from = formatDate(v.from, format);
        val.to = formatDate(date, format);
        // if new 'to' value is before the existing 'from' value, then reset the 'from' value
        if (!!v.from && date < v.from) {
          val.from = "";
          setShow("from");
        } else {
          setShow(null);
        }
      }

      onChange({
        field: name,
        value: val,
        callback: () => {
          onBlur({ field: name, value: val });
        }
      });

      if (isValidDate(from) && isValidDate(to)) setShow(null);
    }
  };

  /*
  ----------
  VALIDATION
  ----------

  validation to allow:
  - an empty field
  - a valid date formatted as MM/DD/YYYY or M/D/YYYY
  */
  // eslint-disable-next-line complexity
  const handleOnValidate = ({ field, value, validation }) => {
    const errors = onValidate({ field, value, validation });
    // const regTest =
    //   /^(0?[1-9]|1[0-2])\/(0?[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$|^$/;

    const f = value.from;
    const t = value.to;
    const fInt = formatDate(f, "epoch");
    const tInt = formatDate(t, "epoch");
    const labelF = labelFrom || "'From date'";
    const labelT = labelTo || "'To date'";

    // is date range required?
    if (
      validation.required &&
      (!isValidDate(value.from) || !isValidDate(value.to))
    ) {
      errors.push("This date range is required.");
    }

    // inputted date is not a valid format
    if (!!f && f !== "__/__/____" && !isValidDate(f)) {
      errors.push(labelFrom + " is not a valid date.");
    }
    if (!!t && t !== "__/__/____" && !isValidDate(t)) {
      errors.push(labelTo + " is not a valid date.");
    }

    // converted date to epoch to compare dates as numbers

    // if both dates are avail, `from` comes before `to`
    if (f.length && t.length && tInt < fInt) {
      errors.push(labelFrom + " cannot be after " + labelT + ".");
    }

    // compare with min date
    if (min && f.length) {
      if (fInt < min)
        errors.push(
          labelF + " cannot be before " + formatDate(min, "M/D/YYYY") + "."
        );
    }
    if (min && t.length) {
      if (tInt < min)
        errors.push(
          labelT + " cannot be before " + formatDate(min, "M/D/YYYY") + "."
        );
    }

    // compare with max date
    if (max && t.length) {
      if (tInt > max)
        errors.push(
          labelT + " cannot be after " + formatDate(max, "M/D/YYYY") + "."
        );
    }
    if (max && f.length) {
      if (fInt > max)
        errors.push(
          labelF + " cannot be after " + formatDate(max, "M/D/YYYY") + "."
        );
    }

    return errors;
  };

  const inputFromClass = classnames({
    "has-error": !!fieldErrors?.length,
    "is-required": required,
    "has-value": !!value.from
  });

  const inputToClass = classnames({
    "has-error": !!fieldErrors?.length,
    "is-required": required,
    "has-value": !!value.to
  });

  useField({
    name,
    validation: { required, requiredError },
    onValidate: handleOnValidate,
    label: "Date Range"
  });

  const fv = isValidDate(value?.from)
    ? formatDate(value.from, "MM/DD/YYYY")
    : value.from || "";
  const tv = isValidDate(value?.to)
    ? formatDate(value.to, "MM/DD/YYYY")
    : value.to || "";

  const prevShow = useRef({ show }).current;

  useEffect(() => {
    if (!show && prevShow.show === "from")
      // blur from
      handleFromBlur();

    if (!show && prevShow.show === "to")
      // blur to
      handleToBlur();

    return () => (prevShow.show = show);
  }, [handleFromBlur, handleToBlur, prevShow, show]);

  return (
    <div id={id}>
      <div
        ref={mainRef}
        className={classnames("uikit__daterange-wrapper", {
          "uikit__hide-label": hideLabel,
          "uikit__form__hide-errors": hideErrors
        })}
        {...createDataAttributes([
          ...(dataWrapperAttrib || []),
          { name: "focused", value: show }
        ])}
      >
        <div
          className={classnames(
            "uikit__datepicker-wrapper",
            "uikit__form-item"
          )}
        >
          {!noLabel && (
            <Label
              htmlFor={`${id}-from`}
              label={labelFrom}
              required={required}
            />
          )}
          <div className="uikit__datepicker-wrapper-inner-from">
            <div className="uikit__datepicker-inner">
              <div className="uikit__datepicker__input-wrapper">
                <InputMask
                  mask="99/99/9999"
                  id={`${id}-from`}
                  name={name}
                  value={fv}
                  disabled={disabled}
                  onFocus={handleFromFocus}
                  onChange={handleFromChange}
                  onBlur={handleFromBlur}
                  onKeyDown={handleKeyDown}
                  className={inputFromClass}
                  placeholder={placeholder}
                  {...createDataAttributes(dataFromAttrib)}
                  {...rest}
                />
                {!!value?.from && !disabled && (
                  <>
                    <div
                      id={`datepicker-clear-${id}-from`}
                      className="uikit-sr-only"
                    >
                      Clear
                    </div>
                    <button
                      aria-labelledby={`datepicker-clear-${id}-from`}
                      tabIndex="-1"
                      type="button"
                      className="uikit__datepicker__clear"
                      onClick={() => handleFromClear()}
                    >
                      <Icon icon="fasTimes" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div
          className={classnames(
            "uikit__datepicker-wrapper",
            "uikit__form-item"
          )}
        >
          {!noLabel && (
            <Label htmlFor={`${id}-to`} label={labelTo} required={required} />
          )}
          <div className="uikit__datepicker-wrapper-inner-to">
            <div className="uikit__datepicker-inner">
              <div className="uikit__datepicker__input-wrapper">
                <InputMask
                  mask="99/99/9999"
                  id={`${id}-to`}
                  name={name}
                  value={tv}
                  disabled={disabled}
                  onFocus={handleToFocus}
                  onChange={handleToChange}
                  onBlur={handleToBlur}
                  onKeyDown={handleKeyDown}
                  className={inputToClass}
                  placeholder={placeholder}
                  {...createDataAttributes(dataFromAttrib)}
                  {...rest}
                />
                {!!value?.to && !disabled && (
                  <>
                    <div
                      id={`datepicker-clear-${id}-to`}
                      className="uikit-sr-only"
                    >
                      Clear
                    </div>
                    <button
                      aria-labelledby={`datepicker-clear-${id}-to`}
                      tabIndex="-1"
                      type="button"
                      className="uikit__datepicker__clear"
                      onClick={() => handleToClear()}
                    >
                      <Icon icon="fasTimes" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        {show && (
          <Calendar
            mode="single"
            setShow={setShow}
            show={show}
            value={value}
            mainRef={mainRef}
            handleDaySelect={handleDaySelect}
            minDate={min}
            maxDate={max}
          />
        )}
      </div>
      {!show && !hideErrors && !!fieldErrors?.length && (
        <ErrorsForField id={`errors_for_${id}`} errors={fieldErrors} />
      )}
    </div>
  );
};

DateRange.propTypes = {
  //global form props
  className: propTypes.string,
  disabled: propTypes.bool,

  id: propTypes.string.isRequired,
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  name: propTypes.string.isRequired,
  onBlur: propTypes.func.isRequired,
  onChange: propTypes.func.isRequired,
  onKeyDown: propTypes.func,
  onValidate: propTypes.func.isRequired,
  required: propTypes.bool,
  value: propTypes.object,
  hideLabel: propTypes.bool,
  noLabel: propTypes.bool,
  requiredError: propTypes.string,
  showPreviousMonths: propTypes.bool,

  fieldErrors: propTypes.array,
  hideErrors: propTypes.bool,

  //component-specific
  minDate: propTypes.oneOfType([
    propTypes.string,
    propTypes.instanceOf(Date),
    propTypes.object
  ]),
  maxDate: propTypes.oneOfType([
    propTypes.string,
    propTypes.instanceOf(Date),
    propTypes.object
  ]),
  format: propTypes.string,
  labelFrom: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  labelTo: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  componentname: propTypes.string,
  dataFromAttrib: propTypes.arrayOf(Object),
  dataToAttrib: propTypes.arrayOf(Object),
  dataWrapperAttrib: propTypes.arrayOf(Object),
  placeholder: propTypes.string
};

DateRange.defaultProps = {
  placeholder: "MM/DD/YYYY",
  hideErrors: false,
  componentname: "DateRange",
  showPreviousMonths: false
};

export default DateRange;
